import { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Mail, Phone, MapPin, Facebook, Instagram, Twitter, ArrowUpRight } from 'lucide-react';

const quickLinks = [
  { label: 'Home', href: '/' },
  { label: 'About Us', href: '/about' },
  { label: 'Programs', href: '#programs' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Contact', href: '#contact' },
];

const socials = [
  { Icon: Facebook, label: 'Facebook', href: '#' },
  { Icon: Instagram, label: 'Instagram', href: '#' },
  { Icon: Twitter, label: 'Twitter', href: '#' },
];

const contactItems = [
  { Icon: MapPin, label: 'Visit', value: '35 Otunba Akin Joacquin St, Ago Palace Way, Okota, Lagos' },
  { Icon: Phone, label: 'Call', value: '08051283366' },
  { Icon: Mail, label: 'Email', value: 'info@renaissancegardenschools.com' },
];

const Footer = () => {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end end'] });

  // Subtle parallax shift upward as footer comes into view
  const bgY = useTransform(scrollYProgress, [0, 1], ['8%', '0%']);

  const isInView = useInView(ref, { once: true, margin: '-80px 0px' });
  const year = new Date().getFullYear();

  return (
    <footer ref={ref} className="relative bg-[#06060e] text-white overflow-hidden pt-16 sm:pt-24 pb-8 sm:pb-10">
      {/* ── Parallax background glow ── */}
      <motion.div
        style={{ y: bgY }}
        aria-hidden
        className="absolute inset-0 will-change-transform pointer-events-none"
      >
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/6 rounded-full blur-[130px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-600/5 rounded-full blur-[100px]" />
        {/* Dot pattern */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
            backgroundSize: '42px 42px',
          }}
        />
      </motion.div>

      {/* Top accent line */}
      <div className="absolute top-0 inset-x-0 h-[1px] bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

      <div className="container mx-auto px-6 relative z-10">
        {/* ── Main grid ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 sm:gap-12 lg:gap-14 mb-12 sm:mb-20">
          {/* Brand column */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 space-y-8"
          >
            <a href="/" className="flex items-center gap-4 group w-fit">
              <div className="p-2.5 bg-white rounded-2xl shadow-xl group-hover:shadow-2xl transition-shadow">
                <img
                  src="/lovable-uploads/859cd9be-32fe-431f-a361-89e4061203fb.png"
                  alt="Renaissance Garden Schools Logo"
                  className="h-10 w-auto"
                />
              </div>
              <span className="font-display font-black text-2xl tracking-tight text-white">
                Renaissance Garden <span className="text-primary">Schools</span>
              </span>
            </a>

            <p className="text-slate-300 text-base font-medium leading-relaxed max-w-sm">
              Nurturing young minds and building tomorrow's leaders with passion, purpose,
              and excellence since 2006.
            </p>

            {/* Social links */}
            <div className="flex gap-3">
              {socials.map(({ Icon, href, label }, i) => (
                <motion.a
                  key={i}
                  aria-label={label}
                  href={href === '#' ? undefined : href}
                  aria-disabled={href === '#'}
                  whileHover={{ y: -4, scale: 1.12 }}
                  className="w-11 h-11 rounded-xl bg-white/5 border border-white/8 flex items-center justify-center text-slate-300 hover:bg-primary hover:text-white hover:border-transparent transition-all"
                >
                  <Icon size={17} />
                </motion.a>
              ))}
            </div>

            {/* Motto */}
            <p className="text-slate-400 text-[11px] font-bold tracking-[0.25em] uppercase italic">
              "Our God, Our Vision, Our Strength"
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-3 space-y-8"
          >
            <h5 className="text-[9px] font-black uppercase tracking-[0.35em] text-slate-400">
              Explore
            </h5>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <motion.a
                    href={link.href.startsWith('#') ? '/'+link.href : link.href}
                    whileHover={{ x: 6 }}
                    className="flex items-center gap-2 text-white/60 font-semibold text-[15px] hover:text-white transition-colors group"
                  >
                    <span>{link.label}</span>
                    <ArrowUpRight
                      size={13}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-primary"
                    />
                  </motion.a>
                </li>
              ))}
              <li>
                <motion.a
                  href="https://portal.renaissancegardenschools.com?school=rgs"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ x: 6 }}
                  className="flex items-center gap-2 text-primary/70 font-semibold text-[15px] hover:text-primary transition-colors group"
                >
                  <span>Student Portal</span>
                  <ArrowUpRight
                    size={13}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </motion.a>
              </li>
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-4 space-y-8"
          >
            <h5 className="text-[9px] font-black uppercase tracking-[0.35em] text-slate-400">
              Contact
            </h5>
            <div className="space-y-5">
              {contactItems.map(({ Icon, label, value }, i) => (
                <div key={i} className="flex gap-4 group">
                  <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/8 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/15 transition-colors">
                    <Icon size={16} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-[0.28em] text-slate-400 mb-1">
                      {label}
                    </p>
                    <p className="text-white/65 font-semibold text-[14px] leading-snug">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ── Bottom bar ── */}
        <div className="pt-8 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-400 text-sm font-medium">
            &copy; {year} Renaissance Garden Schools. All rights reserved.
          </p>
          <div className="flex gap-6">
            {['Privacy Policy', 'Terms of Service'].map((t) => (
              <a
                key={t}
                href="#"
                className="text-slate-400 text-sm font-medium hover:text-white/70 transition-colors"
              >
                {t}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
